import { Injectable } from '@angular/core';
import { PermissionType, ResourceNames } from '@enums/acl/resources.enum';
import { SystemProfileRoleI } from '@interfaces/system-profile.interface';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AclAbilityService {
  private static _rolesChanges = new BehaviorSubject<any | undefined>(undefined);
  public static rolesChanges$ = AclAbilityService._rolesChanges.asObservable();
  private roles: SystemProfileRoleI[] = [];

  public canRead(resource: ResourceNames): boolean {
    return this.can(PermissionType.READ, resource);
  }

  public canListAll(resource: ResourceNames): boolean {
    return this.can(PermissionType.LIST_ALL, resource);
  }

  public canFinish(resource: ResourceNames): boolean {
    return this.can(PermissionType.FINISH, resource);
  }

  public canVerification(resource: ResourceNames) {
    return this.can(PermissionType.VERIFICATION, resource);
  }

  public canTransfer(resource: ResourceNames): boolean {
    return this.can(PermissionType.TRANSFER, resource);
  }

  public canCreate(resource: ResourceNames): boolean {
    return this.can(PermissionType.CREATE, resource);
  }

  public canDelete(resource: ResourceNames): boolean {
    return this.can(PermissionType.DELETE, resource);
  }

  public canUpdate(resource: ResourceNames): boolean {
    return this.can(PermissionType.UPDATE, resource);
  }

  public can(permission: string, resource: string): boolean {
    let roles = this.getRoles();
    if (roles.length == 0) {
      // A permission predicate must stay side-effect free: this runs constantly for menu/ACL
      // rendering, and wiping localStorage here turned any transient empty-roles read (e.g.
      // mid session-write) into a full session wipe. Auth guards own logout.
      return false;
    }
    const idx = roles.findIndex((e: any) => e.resource == resource);
    if (idx !== -1) {
      const permissions: Array<any> = roles[idx].permissions || [];
      const result = permissions.includes(permission);
      return result;
    }
    return false;
  }
  currentProfile(profiles: Array<any>): any {
    let maxIdx = 0;
    let maxLevel = -1;
    for (let i = 0; i < profiles.length; i++) {
      const profile = profiles[i];
      if (typeof profile == 'object') {
        const level = profile['level'] || 0;
        if (level > maxLevel) {
          maxLevel = level;
          maxIdx = i;
        }
      }
    }
    return profiles[maxIdx];
  }

  private getUserId(): string {
    let user = atob(localStorage.getItem(btoa('userId')) || '');
    return user;
  }
  public saveRoles(roles: Array<SystemProfileRoleI>, userId: string) {
    this.saveUserId(userId);
    this.roles = roles;
    const key = this.getRolesKey();

    if (key) {
      const value = btoa(JSON.stringify(roles));
      localStorage.setItem(key, value);
    }
    this.notifyChanges();
  }

  public refresh(userId: string) {
    this.saveUserId(userId);
    this.roles = [];
    this.notifyChanges();
  }

  private notifyChanges() {
    AclAbilityService._rolesChanges.next(this.getRoles());
  }

  private saveUserId(userId: string) {
    localStorage.setItem(btoa('userId'), btoa(userId));
  }

  private getRolesKey(): string {
    const userId = this.getUserId();
    if (userId) {
      const key = btoa(`roles_${userId}`);
      return key;
    }
    return '';
  }

  private getRoles(): Array<any> {
    if (!this.roles.length) {
      const key = this.getRolesKey();
      if (key) {
        const rolesStr = localStorage.getItem(key);
        if (rolesStr) {
          try {
            this.roles = JSON.parse(atob(rolesStr));
          } catch (Err) {}
        }
      }
    }
    return this.roles;
  }

  public mergePermissions(listToMerge: Array<Array<SystemProfileRoleI>>): Array<SystemProfileRoleI> {
    const result: Array<SystemProfileRoleI> = [];
    for (let roles of listToMerge) {
      for (let role of roles) {
        const idx = result.findIndex((elem) => elem.resource == role.resource);
        if (idx == -1) {
          result.push(role);
        } else {
          const item = result[idx];
          for (let permission of role.permissions) {
            if (!item.permissions.includes(permission)) {
              item.permissions.push(permission);
            }
          }
        }
      }
    }
    return result;
  }

  public removeRolesCache() {
    const key = this.getRolesKey();
    if (key) {
      localStorage.removeItem(key);
      this.roles = [];
    }
  }
}
